import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchLatestTwaVersion,
  isTrustedApkUrl,
  type TwaVersionDescriptor,
} from '@/services/twa-version.service'
import { getInstalledTwaWrapper, isMultipackTwaSession } from '@/utils/twa-wrapper'

const DISMISS_KEY = 'pmms.twa.update.dismissed'
/** Re-prompt for the same dismissed version after this interval. */
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000
/** Foreground re-check cooldown. */
const FOREGROUND_CHECK_MS = 6 * 60 * 60 * 1000

type DismissState = { versionCode: number; at: number }

function readDismissed(): DismissState | null {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as DismissState
    if (!parsed || typeof parsed.versionCode !== 'number' || typeof parsed.at !== 'number') {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeDismissed(versionCode: number) {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ versionCode, at: Date.now() }))
  } catch {
    // Ignore quota / private mode.
  }
}

function shouldSuppress(latestCode: number, mandatory: boolean): boolean {
  if (mandatory) return false
  const dismissed = readDismissed()
  if (!dismissed) return false
  if (dismissed.versionCode !== latestCode) return false
  return Date.now() - dismissed.at < DISMISS_TTL_MS
}

/**
 * Sideload APK update checks for MultiPack TWA only.
 * Independent of web `version.json` / useAppUpdate.
 */
export function useTwaWrapperUpdate() {
  const [latest, setLatest] = useState<TwaVersionDescriptor | null>(null)
  const [visible, setVisible] = useState(false)
  const installed = useMemo(
    () => (isMultipackTwaSession() ? getInstalledTwaWrapper() : null),
    [],
  )

  const evaluate = useCallback(async () => {
    if (!installed) {
      setVisible(false)
      setLatest(null)
      return
    }

    const remote = await fetchLatestTwaVersion()
    if (!remote) {
      setVisible(false)
      return
    }

    setLatest(remote)

    const behind = remote.versionCode > installed.versionCode
    const forced =
      remote.mandatory && installed.versionCode < remote.minRequiredVersionCode

    if (!behind && !forced) {
      setVisible(false)
      return
    }

    if (shouldSuppress(remote.versionCode, forced)) {
      setVisible(false)
      return
    }

    setVisible(true)
  }, [installed])

  useEffect(() => {
    if (!installed) return

    let cancelled = false
    let lastCheck = 0

    const run = async () => {
      if (cancelled) return
      lastCheck = Date.now()
      await evaluate()
    }

    void run()

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastCheck < FOREGROUND_CHECK_MS) return
      void run()
    }

    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [installed, evaluate])

  const dismiss = useCallback(() => {
    if (!latest || !installed) return
    const forced =
      latest.mandatory && installed.versionCode < latest.minRequiredVersionCode
    if (forced) return
    writeDismissed(latest.versionCode)
    setVisible(false)
  }, [latest, installed])

  const startUpdate = useCallback(() => {
    if (!latest || !isTrustedApkUrl(latest.apkUrl)) return
    window.location.assign(latest.apkUrl)
  }, [latest])

  const mandatory = Boolean(
    latest &&
      installed &&
      latest.mandatory &&
      installed.versionCode < latest.minRequiredVersionCode,
  )

  return {
    isTwa: Boolean(installed),
    installed,
    latest,
    updateAvailable: visible,
    mandatory,
    dismiss,
    startUpdate,
  }
}
